import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { postProjectTemplateTdm } from '@/api/Sherpa/ProjectTemplate';
import type { PostProjectTemplateTdmPayload } from '@/api/Sherpa/ProjectTemplate/types';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { usePhaseCategories } from '@/hooks/ReactQueryHooks/useCategoryQuery';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { useProjectTemplateFileUpload } from '@/hooks/useProjectTemplateFileUpload';
import { notifyUser } from '@/store/Toast';
import type { TitleInterface } from '@/types/Account';
import type { Category } from '@/types/Category';
import { getAcceptedFileTypes, sanitizeResponseError } from '@/utils';

interface INewTaskFormProps {
  handleModal: () => void;
  phase: string;
}

interface ICreateTemplateTaskPayload {
  data: PostProjectTemplateTdmPayload;
  templateId: number;
}

const initialData = {
  task: '',
  categoryId: 0,
  lead: 0,
  support: 0,
  phase: '',
};

const NewTaskForm = ({ phase, handleModal }: INewTaskFormProps) => {
  const pageParams = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { watch } = useFormContext();
  const selectedLead = watch('lead');
  const selectedSupport = watch('support');

  const fetchPhaseCategories = usePhaseCategories({ phase });
  const phaseCategories = fetchPhaseCategories?.data?.data;

  const templateFileRef = useRef<File[]>(null);

  const fetchOrgTitles = useOrgTitles({});
  const titles = fetchOrgTitles?.data?.data;

  const signAndUploadFiles = useProjectTemplateFileUpload('TASK');

  const createTemplateTask = useMutation((payload: ICreateTemplateTaskPayload) => postProjectTemplateTdm(payload), {
    onSuccess: (response: any) => {
      if (templateFileRef.current) {
        signAndUploadFiles.uploadTemplate({
          templateFile: templateFileRef.current,
          tdmId: response.data.id,
          templateId: Number(pageParams.templateId),
        });
        return;
      }
      handleAddSuccess();
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const handleAddSuccess = useCallback(() => {
    queryClient.invalidateQueries(['get-project-template-tdm']);
    handleModal();
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'Project Template task added successfully',
        toastType: 'success',
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, dispatch, handleModal]);

  useEffect(() => {
    if (signAndUploadFiles.uploadSuccess) {
      handleAddSuccess();
    }
  }, [signAndUploadFiles.uploadSuccess, handleAddSuccess]);

  const addTemplateTask = (data: any) => {
    const { task, category, lead, support, note, template } = data;

    const transformedData: PostProjectTemplateTdmPayload = {
      resourceType: 'TASK',
      templateData: {
        task,
        phase: phase.toUpperCase(),
        categoryId: category.id,
        leadTitleId: lead.id,
        supportTitleId: support?.id,
        note,
      },
    };

    if (template?.length) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      data.template?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }

      if (validFiles.length > 0) {
        templateFileRef.current = validFiles;
      }
    }

    createTemplateTask.mutate({
      data: transformedData,
      templateId: Number(pageParams.templateId),
    });
  };

  return (
    <>
      {/* <Modal>
        <HookFormProvider defaultValues={initialData}> */}
      <HookForm noDefaultClass className='w-full' onSubmit={addTemplateTask}>
        <h2 className='m-0 mb-5'>New Task</h2>
        <div className='space-y-5'>
          <HookInput type='text' label='Task Name' name='task' id='name' placeholder='e.g. Sage Office' required />
          <HookReactSingleSelect
            id='category'
            options={phaseCategories}
            getOptionLabel={(option) => (option as Category).name}
            getOptionValue={(option) => (option as Category).id.toString()}
            placeholder='Select Category'
            name='category'
            label='Category'
            required
          />
          <HookReactSingleSelect
            id='leadOrgTitleId'
            options={titles?.filter((data: any) => data?.id !== selectedSupport?.id)}
            getOptionLabel={(option) => (option as TitleInterface).name}
            getOptionValue={(option) => (option as TitleInterface).id.toString()}
            placeholder='Select Lead'
            name='lead'
            label='Lead Organizational Title'
            required
          />
          <HookReactSingleSelect
            id='supportOrgTitleId'
            options={titles?.filter((data: any) => data?.id !== selectedLead?.id)}
            getOptionLabel={(option) => (option as TitleInterface).name}
            getOptionValue={(option) => (option as TitleInterface).id.toString()}
            placeholder='Select support'
            name='support'
            label='Support Organizational Title'
          />
          <HookTextarea name='note' label='Notes' placeholder='Type your notes here...' />
          <HookFileInput label='Template' name='template' id='template' multiple />
          <div className='flex justify-end gap-4'>
            <Button label='Cancel' btnStyle='btn-neutral' type='button' disabled={signAndUploadFiles.isLoading} onClick={handleModal} />
            <Button
              label={`${signAndUploadFiles.isLoading ? 'Creating...' : 'Add'}`}
              disabled={signAndUploadFiles.isLoading}
              btnStyle='btn-primary'
              type='submit'
            />
          </div>
        </div>
      </HookForm>
      {/* </HookFormProvider>
      </Modal> */}
    </>
  );
};

const NewTaskFormProvider = ({ phase, handleModal }: INewTaskFormProps) => {
  return (
    <Modal>
      <HookFormProvider defaultValues={initialData}>
        <NewTaskForm phase={phase} handleModal={handleModal} />
      </HookFormProvider>
    </Modal>
  );
};

export default NewTaskFormProvider;
